import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react'
import { AgGridReact } from '@ag-grid-community/react'
import { ModuleRegistry } from '@ag-grid-community/core'
import type {
  CellClickedEvent,
  CellValueChangedEvent,
  ColDef,
  ICellRendererParams,
} from '@ag-grid-community/core'
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model'
import type { PlanAndRateOverrideParams } from '../../../api/dto/plan-and-rate-override-rules.dto'
import {
  normalizeParams,
  numFormatter,
  PLAN_TYPE_OPTIONS,
  LEVEL_OPTIONS,
  PLAN_STATUS_OPTIONS,
} from './planAndRateOverrideForm.helpers'

ModuleRegistry.registerModules([ClientSideRowModelModule])

export type PlanAndRateParamsSingleRowGridMode = 'create' | 'edit' | 'view'

export interface PlanAndRateParamsSingleRowGridHandle {
  getLatestParams: () => PlanAndRateOverrideParams
}

interface Props {
  params: PlanAndRateOverrideParams
  mode: PlanAndRateParamsSingleRowGridMode
  onChange?: (params: PlanAndRateOverrideParams) => void
}

interface GridContext {
  isReadOnly: boolean
}

const BOOL_FIELDS = new Set([
  'hsa',
  'onExchange',
  'offExchange',
  'isForSale',
  'proofOfResidenceRequired',
])

const RA_KEYS = Array.from(
  { length: 67 },
  (_, i) => `RA${String(i + 1).padStart(2, '0')}`
)
const RA_KEYS_SET = new Set(RA_KEYS)

const DEFAULT_COL_DEF: ColDef = {
  sortable: false,
  filter: false,
  resizable: true,
}

const BoolCellRenderer = ({
  value,
  context,
}: ICellRendererParams & { context: GridContext }) => {
  const { isReadOnly } = context ?? {}
  return (
    <span
      style={{
        cursor: isReadOnly ? 'default' : 'pointer',
        userSelect: 'none',
        color: value === true ? '#52c41a' : '#ff4d4f',
      }}
    >
      {value === true ? '✓' : '✗'}
    </span>
  )
}

const PlanAndRateParamsSingleRowGrid = forwardRef<
  PlanAndRateParamsSingleRowGridHandle,
  Props
>(function PlanAndRateParamsSingleRowGrid({ params, mode, onChange }, ref) {
  const isReadOnly = mode === 'view'
  const gridRef = useRef<AgGridReact<PlanAndRateOverrideParams>>(null)
  const paramsRef = useRef<PlanAndRateOverrideParams>(normalizeParams(params))

  useImperativeHandle(ref, () => ({
    getLatestParams: (): PlanAndRateOverrideParams => {
      gridRef.current?.api.stopEditing()
      return { ...paramsRef.current }
    },
  }))

  const rowData = useMemo<PlanAndRateOverrideParams[]>(() => {
    paramsRef.current = normalizeParams(params)
    return [paramsRef.current]
  }, [params])

  const handleCellClicked = useCallback(
    (event: CellClickedEvent<PlanAndRateOverrideParams>) => {
      if (isReadOnly) return
      const colId = event.column.getColId()
      if (!BOOL_FIELDS.has(colId)) return
      const currentValue = (paramsRef.current as Record<string, unknown>)[colId]
      onChange?.({ ...paramsRef.current, [colId]: currentValue !== true })
    },
    [isReadOnly, onChange]
  )

  const handleCellValueChanged = useCallback(
    (event: CellValueChangedEvent<PlanAndRateOverrideParams>) => {
      const colId = event.column.getColId()
      let newParams: PlanAndRateOverrideParams

      if (RA_KEYS_SET.has(colId)) {
        const raw = event.newValue
        const num =
          raw !== '' && raw != null && raw !== undefined ? Number(raw) : null
        newParams = {
          ...event.data,
          rateAdjustments: {
            ...(event.data.rateAdjustments ?? {}),
            [colId]: Number.isFinite(num) ? num : null,
          },
        }
      } else {
        newParams = { ...event.data }
      }

      paramsRef.current = newParams
      onChange?.(newParams)
    },
    [onChange]
  )

  const context = useMemo<GridContext>(() => ({ isReadOnly }), [isReadOnly])

  const columnDefs = useMemo<ColDef<PlanAndRateOverrideParams>[]>(() => {
    const txt = (
      field: string,
      headerName: string,
      minWidth: number
    ): ColDef<PlanAndRateOverrideParams> => ({
      colId: field,
      field: field as keyof PlanAndRateOverrideParams,
      headerName,
      editable: !isReadOnly,
      cellEditor: 'agTextCellEditor',
      singleClickEdit: true,
      valueFormatter: (p) => (p.value != null ? String(p.value) : '—'),
      minWidth,
    })

    const num = (
      field: string,
      headerName: string,
      minWidth: number
    ): ColDef<PlanAndRateOverrideParams> => ({
      colId: field,
      field: field as keyof PlanAndRateOverrideParams,
      headerName,
      editable: !isReadOnly,
      cellEditor: 'agNumberCellEditor',
      singleClickEdit: true,
      valueFormatter: numFormatter,
      minWidth,
    })

    const sel = (
      field: string,
      headerName: string,
      options: { value: string; label: string }[],
      minWidth: number
    ): ColDef<PlanAndRateOverrideParams> => ({
      colId: field,
      field: field as keyof PlanAndRateOverrideParams,
      headerName,
      editable: !isReadOnly,
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: { values: options.map((o) => o.value) },
      singleClickEdit: true,
      valueFormatter: (p) => {
        if (p.value == null) return '—'
        return (
          options.find((o) => o.value === p.value)?.label ?? String(p.value)
        )
      },
      minWidth,
    })

    const bool = (
      field: string,
      headerName: string,
      minWidth: number
    ): ColDef<PlanAndRateOverrideParams> => ({
      colId: field,
      field: field as keyof PlanAndRateOverrideParams,
      headerName,
      editable: false,
      cellRenderer: BoolCellRenderer,
      minWidth,
    })

    return [
      txt('planName', 'Plan Name', 180),
      txt('carrier', 'Carrier', 130),
      txt('householdName', 'Carrier Friendly Name', 160),
      sel('planType', 'Plan Type', PLAN_TYPE_OPTIONS, 100),
      sel('level', 'Metal Level', LEVEL_OPTIONS, 120),
      txt('serviceAreaId', 'Service Area ID', 130),
      bool('isForSale', 'For Sale', 90),
      bool('hsa', 'HSA', 70),
      txt('networkName', 'Network', 140),
      bool('onExchange', 'On Exchange', 110),
      bool('offExchange', 'Off Exchange', 110),
      sel('planStatus', 'Plan Status', PLAN_STATUS_OPTIONS, 110),
      txt('crosswalkPlanId', 'Crosswalk Plan ID', 150),
      num('baseRate', 'Base Rate', 100),
      num('planFee', 'Plan Fee', 100),
      bool('proofOfResidenceRequired', 'Proof of Residence', 150),
      ...RA_KEYS.map(
        (raKey): ColDef<PlanAndRateOverrideParams> => ({
          colId: raKey,
          headerName: raKey,
          editable: !isReadOnly,
          cellEditor: 'agNumberCellEditor',
          singleClickEdit: true,
          valueGetter: (p) => p.data?.rateAdjustments?.[raKey] ?? null,
          valueSetter: (p) => {
            if (!p.data) return false
            const raw = p.newValue
            const num =
              raw !== '' && raw != null && raw !== undefined
                ? Number(raw)
                : null
            p.data.rateAdjustments = {
              ...(p.data.rateAdjustments ?? {}),
              [raKey]: Number.isFinite(num) ? num : null,
            }
            return true
          },
          valueFormatter: numFormatter,
          minWidth: 80,
        })
      ),
    ]
  }, [isReadOnly])

  return (
    <div className="ag-theme-alpine" style={{ width: '100%', fontSize: 13 }}>
      <AgGridReact
        ref={gridRef}
        rowData={rowData}
        columnDefs={columnDefs}
        defaultColDef={DEFAULT_COL_DEF}
        getRowId={() => 'params'}
        domLayout="autoHeight"
        headerHeight={36}
        rowHeight={36}
        suppressCellFocus={isReadOnly}
        suppressPaginationPanel
        suppressMovableColumns
        stopEditingWhenCellsLoseFocus
        context={context}
        onCellValueChanged={handleCellValueChanged}
        onCellClicked={handleCellClicked}
      />
    </div>
  )
})

export default PlanAndRateParamsSingleRowGrid
export { BOOL_FIELDS }
